import * as analytics from '@anm/analytic';
import api from '@anm/api';
import { VideoInfo } from '@anm/api/modules/hosting';
import {
  CreateProjectParams,
  DeleteProjectParams,
  DuplicateProjectParams,
  MoveProjectParams,
  MoveProjectToTeamParams,
  MoveProjectToUserParams,
  UpdateProjectMeta
} from '@anm/api/modules/projects';
import { Workspace } from '@anm/api/modules/team';
import { DEFAULT_PROJECT_NAME } from '@anm/constants/my-videos';
import cutFileExtension from '@anm/helpers/cutFileExtension';
import getExtensionFromPath from '@anm/helpers/getExtensionFromPath';
import getFrameFromVideo from '@anm/helpers/getFrameFromVideo';
import getVideoInfo, { getVideoPropsFromFile } from '@anm/helpers/getVideoInfo';
import asyncEntity from '@anm/helpers/redux/asyncEntity';
import { call, fork, put, select, take } from '@anm/helpers/saga/effects';
import { takeType } from '@anm/helpers/saga/typesafe-actions';
import { uploadsActions, uploadsSelectors } from '@anm/store/modules/uploads';
import { AnimatronProject } from 'project';

import { videoFolderActions } from '..';
import * as openEditor from '../../../../helpers/openEditor';
import { dashboardTabMap } from '../../../../pages/video';
import routes from '../../../../routes';
import { appMetaSelectors } from '../../appMeta';
import { appNotificationActions } from '../../appNotifications';
import { hostedVideoActions, hostedVideoSelectors } from '../../hostedVideo';
import { projectActions } from '../../project';
import { getVideoNameWithDefault, mapUploadedVideoToProject } from '../helpers';
import {
  selectCreatedProject,
  selectCreatedProjectId,
  selectCurrentFolder,
  selectCurrentFolderId,
  selectCurrentProjects,
  selectNestedProjectByFolderId,
  selectProjectById
} from '../selectors';

import { selectCurrentProject } from './../../project/selectors';
import { cancelCreateProject, removeProjectFromList } from './../actions';
import { updateFolderPreview } from './folderSaga';

const { Router } = routes;

export const defaultVideoInfo: Partial<VideoInfo> = {
  preview: '',
  width: 280,
  height: 162
};

type AddNewProjectProps = {
  hostId: string;
  file?: File;
  folderId?: number;
};

const moveProjectAsync = asyncEntity(videoFolderActions.moveProjectAsync, (params: MoveProjectParams) =>
  api().projects.moveProject(params)
);

const moveProjectToTeamAsync = asyncEntity(
  videoFolderActions.moveProjectToOtherAsync,
  (params: MoveProjectToTeamParams) => api().projects.moveProjectToTeam(params)
);

const moveProjectToUserAsync = asyncEntity(
  videoFolderActions.moveProjectToOtherAsync,
  (params: MoveProjectToUserParams) => api().projects.moveProjectToUser(params)
);

const deleteProjectAsync = asyncEntity(videoFolderActions.deleteProjectAsync, (params: DeleteProjectParams) =>
  api().projects.deleteProject(params)
);

const updateProjectMetaAsync = asyncEntity(videoFolderActions.updateProjectMetaAsync, (params: UpdateProjectMeta) =>
  api().projects.updateProjectMeta(params)
);

export const createProjectAsync = asyncEntity(videoFolderActions.createProjectAsync, (params: CreateProjectParams) =>
  api().projects.createProject(params)
);

type UpdateProjectDraftStatusRequestProps = { projects: AnimatronProject[]; workspace: Workspace };
const updateProjectDraftStatusRequest = async ({ projects, workspace }: UpdateProjectDraftStatusRequestProps) => {
  const requests = projects.map(({ id }) => api().projects.rehash({ projectId: id, workspace }));
  const hashResponses = await Promise.all(requests);

  return hashResponses.map((hash, i) => {
    const {
      id,
      extra: { hashHosted }
    } = projects[i];
    return { id, isDraft: hash !== hashHosted } as Partial<AnimatronProject>;
  });
};

const updateProjectDraftStatusAsync = asyncEntity(
  videoFolderActions.updateProjectAsync,
  (props: UpdateProjectDraftStatusRequestProps) => updateProjectDraftStatusRequest(props)
);

const duplicateProjectAsync = asyncEntity(videoFolderActions.duplicateProjectAsync, (params: DuplicateProjectParams) =>
  api().projects.duplicateProject(params)
);

function* watchMoveProject() {
  while (true) {
    const { payload } = yield* take(takeType(videoFolderActions.moveProject));
    const workspace = yield* select(appMetaSelectors.selectWorkspace);
    const params = { ...payload, workspace };

    yield* fork(moveProjectAsync, params, params);
    yield* take(takeType(videoFolderActions.moveProjectAsync.success));
    yield* fork(updateFolderPreview, payload.folderId);
  }
}

function* watchMoveProjectToTeam() {
  while (true) {
    const { payload: params } = yield* take(takeType(videoFolderActions.moveProjectToTeam));
    yield* fork(moveProjectToTeamAsync, params, params);
  }
}

function* watchMoveProjectToUser() {
  while (true) {
    const { payload: params } = yield* take(takeType(videoFolderActions.moveProjectToUser));
    yield* fork(moveProjectToUserAsync, params, params);
  }
}

function* watchDeleteProject() {
  while (true) {
    const { payload: projectId } = yield* take(takeType(videoFolderActions.deleteProject));
    const workspace = yield* select(appMetaSelectors.selectWorkspace);
    const params = { projectId, workspace };

    yield* fork(deleteProjectAsync, params, params);
  }
}

function* watchDeleteProjectSuccess() {
  yield* take(takeType(videoFolderActions.deleteProjectAsync.success));

  yield* put(projectActions.decrementProjectCount());
}

function* watchDeleteProjectFailure() {
  yield* take(takeType(videoFolderActions.deleteProjectAsync.failure));

  yield* put(
    appNotificationActions.notify({
      type: 'error',
      timeout: 5000,
      title: 'Could not delete project'
    })
  );
}

function* watchUpdateProjectMeta() {
  while (true) {
    const { payload } = yield* take(takeType(videoFolderActions.updateProjectMeta));
    const workspace = yield* select(appMetaSelectors.selectWorkspace);
    const params = { ...payload, workspace };

    analytics.trackVideoRename();

    yield* fork(updateProjectMetaAsync, params, params);
  }
}

function* getUpdateDraftProjects() {
  const projects = yield* select(selectCurrentProjects);
  const isHashActual = ({ extra, version }: AnimatronProject) =>
    !extra || !extra.hostingId || version === (extra.hashVersion || 0);

  const needUpdateDraftProjects = projects?.filter(project => !isHashActual(project));

  return needUpdateDraftProjects;
}

function* updateProjectDraftStatus() {
  const projects = yield* call(getUpdateDraftProjects);
  const workspace = yield* select(appMetaSelectors.selectWorkspace);

  if (!!projects) {
    yield* call(updateProjectDraftStatusAsync, { projects, workspace });
  }
}

function* watchUpdateProjectAllStatuses() {
  while (true) {
    yield* take(takeType(videoFolderActions.updateProjectAllStatuses));

    yield* fork(updateProjectDraftStatus);
  }
}

function* defaultDuplicateProjectParams(projectId: string) {
  const currentFolder: ReturnType<typeof selectCurrentFolder> = yield* select(selectCurrentFolder);

  if (!currentFolder) throw new Error('No current folder');

  const { id: folderId } = currentFolder;

  const project: ReturnType<typeof selectProjectById> = yield* select(state => selectProjectById(state, projectId));

  const currentProject: AnimatronProject | null = yield* select(selectCurrentProject);

  if (!project && !currentProject) {
    throw new Error(`Project: ${projectId} - not found`);
  }

  const newName = `${project?.name || currentProject?.name || DEFAULT_PROJECT_NAME} - Copy`;

  return { folderId, newName } as Partial<DuplicateProjectParams>;
}

function* watchDuplicateProject() {
  while (true) {
    const { payload: params }: ReturnType<typeof videoFolderActions.duplicateProject> = yield* take(
      takeType(videoFolderActions.duplicateProject)
    );

    const { folderId, newName }: Pick<DuplicateProjectParams, 'folderId' | 'newName'> = yield call(
      defaultDuplicateProjectParams,
      params.projectId
    );

    const workspace = yield* select(appMetaSelectors.selectWorkspace);
    const newParams: DuplicateProjectParams = { workspace, folderId, newName, ...params };

    analytics.trackDuplicateVideo();

    yield* fork(duplicateProjectAsync, newParams, newParams);
  }
}

function* editProject(projectId: string) {
  openEditor.edit(projectId);
}

function* watchDuplicateAndEditProject() {
  while (true) {
    const { payload: params }: ReturnType<typeof videoFolderActions.duplicateAndOpenEditorProject> = yield* take(
      takeType(videoFolderActions.duplicateAndOpenEditorProject)
    );
    yield* put(videoFolderActions.duplicateProject(params));
    const { payload: newProject } = yield* take(takeType(videoFolderActions.duplicateProjectAsync.success));

    if (params.isOpenNewProject) {
      Router.pushRoute('video', { projectId: newProject.id, tab: dashboardTabMap[0] });
    }

    yield* call(editProject, newProject.id);
  }
}

function* addNewProject({ file, folderId: folderIdToUpload, hostId }: AddNewProjectProps) {
  const currentFolderId = yield* select(selectCurrentFolderId);
  const folderId = folderIdToUpload || currentFolderId;

  if (!folderId || !file) return;

  const cutVideoName = cutFileExtension(file.name);
  const name = getVideoNameWithDefault(cutVideoName);

  try {
    const newVideo: VideoInfo = yield getVideoInfo(getVideoPropsFromFile(file));
    const newProject = mapUploadedVideoToProject({ ...newVideo, name, id: hostId });

    yield* put(
      videoFolderActions.addProject({
        ...newProject,
        folderId
      })
    );
    yield* call(updateCreatedProjectPreview, hostId, file);
  } catch (e) {
    const newProject = mapUploadedVideoToProject({
      ...defaultVideoInfo,
      name,
      id: hostId
    });
    yield* put(videoFolderActions.addProject({ ...newProject, folderId }));
    yield* call(updateCreatedProjectPreview, hostId, file);
  }
}

function* updateCreatedProjectPreview(id: string, file?: File) {
  const folderId = yield* select(selectCurrentFolderId);

  if (!folderId || !file) {
    return;
  }

  const project = yield* select(state =>
    selectNestedProjectByFolderId(state, {
      folderId,
      projectId: id
    })
  );

  if (!project) throw new Error(`Can not find project with id: ${id}`);

  try {
    const preview = yield* call(getFrameFromVideo, { file, frameMaxHeight: project.height });
    yield* put(videoFolderActions.updateCreatedProject({ id, preview }));
  } catch {
    yield* put(
      videoFolderActions.updateCreatedProject({
        id,
        shouldUpdatePreview: true,
        preview: defaultVideoInfo.preview!
      })
    );
  }
}

// function* watchCreateVideoSuccess() {
//   while (true) {
//     yield* take(takeType(hostedVideoActions.createVideoAsync.success));
//     const { hostId, uploadType }: CreateVideoData = yield* select(selectCreatedVideoData);

//     const isUpload = yield* select(hostedVideoSelectors.selectIsUploadCreatedVideo);

//     if (!hostId || uploadType == 'replace') return;

//     if (isUpload) {
//       yield* call(addNewProject, hostId);
//     } else {
//       yield* put(videoFolderActions.updateProjectHostingId(hostId));
//     }
//   }
// }

function* watchCreateUploadSuccess() {
  while (true) {
    yield* take(takeType(uploadsActions.createUploadAsync.success));
    const { hostId, file, folderId, uploadType, isUploadFile } = yield* select(uploadsSelectors.selectLatestUpload);

    if (!hostId || uploadType == 'replace') return;

    if (isUploadFile) {
      yield* call(addNewProject, { hostId, file, folderId });
    } else {
      yield* put(videoFolderActions.updateProjectHostingId({ hostId, projectId: hostId }));
    }
  }
}

function* watchCancelUploadVideo() {
  while (true) {
    const { payload }: ReturnType<typeof uploadsActions.cancelUploading> = yield* take(
      takeType(uploadsActions.cancelUploading)
    );

    if (payload.hostId) {
      yield* put(cancelCreateProject(payload.hostId));
    }
  }
}

function* watchCreateProject() {
  while (true) {
    yield* take(takeType(videoFolderActions.createProject));

    yield* call(createProject);
  }
}

function* createProject() {
  const { hostId, uploadId, folderId: folderIdToCreateProject, uploadType } = yield* select(
    uploadsSelectors.selectLatestUpload
  );

  if (uploadType !== 'new' || !uploadId || !hostId) return;

  const workspace = yield* select(appMetaSelectors.selectWorkspace);

  const currentFolderId = yield* select(selectCurrentFolderId);
  const folderId = folderIdToCreateProject || currentFolderId;

  const params: CreateProjectParams = {
    uploadId,
    workspace,
    hostingId: hostId,
    folderId: folderId || undefined
  };

  yield* call(createProjectAsync, params, params);
}

function* trackCreationEvent(status: 'ok' | 'failed', error?: string) {
  const { project } = yield* select(selectCreatedProject);
  const projectId = yield* select(selectCreatedProjectId);

  const { file } = yield* select(uploadsSelectors.selectLatestUpload);
  if (!file || !project) return;
  const extension = getExtensionFromPath(file.name) || '';

  const { width, height, duration } = project;
  const isStatusOK = status === 'ok';

  isStatusOK && projectId && analytics.updateUserProfile({ LastUploadedVideoId: projectId });

  analytics.trackCreateProjectFromUpload({
    error,
    width,
    height,
    duration,
    extension,
    size: file.size,
    uploadStatus: 'ok',
    projectStatus: status
  });

  if (!isStatusOK) {
    yield put(uploadsActions.clearUploadedFiles());
    yield put(videoFolderActions.clearCreatedProject());
  }
}

function* watchCreateProjectSuccess() {
  while (true) {
    yield* take(takeType(videoFolderActions.createProjectAsync.success));

    yield* call(trackCreationEvent, 'ok');

    yield* put(projectActions.incrementProjectCount());

    yield* put(
      appNotificationActions.notify({
        type: 'success',
        timeout: 5000,
        description: 'Your video has been successfully uploaded'
      })
    );
  }
}

function* watchCreateProjectFailure() {
  while (true) {
    const { payload: error }: ReturnType<typeof videoFolderActions.createProjectAsync.failure> = yield* take(
      takeType(videoFolderActions.createProjectAsync.failure)
    );

    yield* call(trackCreationEvent, 'failed', error.message);

    yield put(
      appNotificationActions.notify({
        type: 'error',
        timeout: 5000,
        description: "Couldn't upload your video. Please, try again later"
      })
    );
  }
}

function* watchSaveEmbedStatusSuccess() {
  while (true) {
    yield* take(takeType(hostedVideoActions.saveEmbedStatusAsync.success));

    const id = yield* select(hostedVideoSelectors.selectHostedVideoId);
    const isEmbeded = yield* select(hostedVideoSelectors.selectEmbedStatus);

    if (!id) return;

    const params = {
      projectIds: id,
      embed: isEmbeded
    };

    yield* put(videoFolderActions.updateEmbedStatus(params));
  }
}

function* watchUploadVideoFailure() {
  while (true) {
    yield* take(hostedVideoActions.uploadVideoAsync.failure);

    const newlyCreatedProjectId = yield* select(hostedVideoSelectors.selectUploadVideoId);

    if (newlyCreatedProjectId) {
      yield* put(removeProjectFromList(newlyCreatedProjectId));
    }
  }
}

function* watchFailures() {
  while (true) {
    const {
      payload: { code, message }
    } = yield* take([takeType(videoFolderActions.moveProjectToOtherAsync.failure)]);

    if (code && message) {
      yield* put(
        appNotificationActions.notify({
          type: 'error',
          timeout: 5000,
          title: message
        })
      );
    }
  }
}

const getProjectWatchers = () => [
  call(watchFailures),
  call(watchMoveProject),
  call(watchDeleteProject),
  call(watchCreateProject),
  call(watchDuplicateProject),
  call(watchUpdateProjectMeta),
  call(watchCancelUploadVideo),
  // call(watchCreateVideoSuccess),
  call(watchCreateUploadSuccess),
  // call(watchUploadVideoSuccess),
  call(watchUploadVideoFailure),
  call(watchMoveProjectToTeam),
  call(watchMoveProjectToUser),
  call(watchDeleteProjectFailure),
  call(watchCreateProjectFailure),
  call(watchDeleteProjectSuccess),
  call(watchCreateProjectSuccess),
  call(watchSaveEmbedStatusSuccess),
  call(watchDuplicateAndEditProject),
  call(watchUpdateProjectAllStatuses)
];

export default getProjectWatchers;
